import { gql, useQuery } from "@apollo/client";
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from "@headlessui/react";
import { MapView, ImageView } from "components/cards";
import { Loader } from "components";
import moment from "moment";
import numeral from "numeral";

const GET_BILLS = gql`
  query GetServicePointPayments(
    $servicePoint: ID
    $page: Int
    $pageSize: Int
    $sort: String
  ) {
    items: getPayments(
      servicePoint: $servicePoint
      page: $page
      pageSize: $pageSize
      sort: $sort
    ) {
      _id
      amount
      paidAt
      origin
      type
      status
      createdAt
    }
  }
`;

interface PaymentsContainerProps {
  servicePoint: string;
}

export const PaymentsContainer = ({ servicePoint }: PaymentsContainerProps) => {
  const { loading, data } = useQuery(GET_BILLS, {
    notifyOnNetworkStatusChange: false,
    variables: {
      servicePoint,
      sort: "-createdAt",
      page: 1,
      pageSize: 10,
    },
  });

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  return data?.items?.length > 0 ? (
    <div className="flex-1 p-6 flow-root overflow-y-auto">
      <ul className="space-y-4">
        {data?.items?.map((historyItem: any) => (
          <HistoryDetails key={historyItem._id} history={historyItem} />
        ))}
      </ul>
    </div>
  ) : (
    <div className="flex min-h-[65vh] items-center justify-center">
      <div className="text-center">
        <svg
          className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-300"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            vectorEffect="non-scaling-stroke"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z"
          />
        </svg>
        <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
          No Payments Yet
        </h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Kindly Refer To CMS For Payment History.
        </p>
      </div>
    </div>
  );
};

const HistoryDetails = ({ history }: { history: any }) => {
  return (
    <>
      <Disclosure
        as="div"
        className="relative border border-dashed grid-cols-4 border-gray-300 rounded cursor-pointer hover:bg-gray-100 w-full text-left"
      >
        <DisclosureButton
          as="div"
          disabled
          className="grid grid-cols-4 gap-4  p-4"
        >
          <div>
            <span className="block text-xs font-light"> Date</span>
            <div className="mt-1 block w-full sm:text-sm">
              {history?.createdAt
                ? moment(history.createdAt).format("DD/MM/YYYY")
                : "N/A"}
            </div>
          </div>
          <div>
            <span className="block text-xs font-light"> Amount</span>
            <div className="mt-1 block w-full sm:text-sm">
              GHS {numeral(history.amount).format("0,0.00")}
            </div>
          </div>
          <div>
            <span className="block text-xs font-light">Type</span>
            <div className="mt-1 block w-full sm:text-sm">
              {history?.type || "N/A"}
            </div>
          </div>
          <div>
            <span className="block text-xs font-light">Origin</span>
            <div className="mt-1 block w-full sm:text-sm">
              {history?.origin || "N/A"}
            </div>
          </div>
        </DisclosureButton>
        <DisclosurePanel as="div" className="grid grid-cols-4 gap-4 px-4 pb-4">
          <div className="col-span-2">
            <span className="text-xs font-light mb-1">Reading Image</span>
            <div className="mt-1 block w-full sm:text-sm">
              <ImageView url={history?.meta?.readingImageUrl} />
            </div>
          </div>
          <div className="col-span-2">
            <span className="text-xs font-light mb-1">Reading Location</span>
            <div className="mt-1 block w-full sm:text-sm">
              <MapView
                latitude={history?.meta?.readingLocation?.coordinates[1] || 0}
                longitude={history?.meta?.readingLocation?.coordinates[0] || 0}
              />
            </div>
          </div>
        </DisclosurePanel>
      </Disclosure>
    </>
  );
};
